import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Building2, Users, BarChart3, Shield, Truck, Package, ArrowRight, LogIn } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Business = () => {
  const navigate = useNavigate();
  const [logisticsVisible, setLogisticsVisible] = useState(false);

  useEffect(() => {
    const fetchFlags = async () => {
      try {
        const api = (await import('../utils/api')).default;
        const res = await api.get('/feature-flags');
        setLogisticsVisible(res.data.logistics_visible);
      } catch (e) {}
    };
    fetchFlags();
  }, []);
  
  const handleGetStarted = () => {
    toast({
      title: 'Get Started',
      description: 'Contact business@tekstar.com to set up your business account and get started.',
    });
  };

  const handleRequestDemo = () => {
    toast({
      title: 'Request a Demo',
      description: 'Our team will contact you soon to schedule a personalized demo. Email: business@tekstar.com',
    });
  };

  const handleContactSales = () => {
    toast({
      title: 'Contact Sales',
      description: 'Reach out to our enterprise sales team at enterprise@tekstar.com for custom pricing.',
    });
  };

  const handleLogin = () => {
    // Business users should use the admin login for now
    // In the future, a dedicated business portal will be available
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Logistics Banner */}
      {logisticsVisible && (
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Truck className="h-6 w-6" />
              <div>
                <p className="font-semibold">NEW: Tekstar Logistics</p>
                <p className="text-sm text-orange-100">Transport your cargo with our trusted operators</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/logistics')}
              className="bg-white text-orange-600 hover:bg-orange-50"
              data-testid="logistics-banner-btn"
            >
              <Package className="h-4 w-4 mr-2" />
              Book Cargo Transport
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
      )}

      {/* Hero Section */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 mb-12">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Tekstar for
              <br />
              <span className="text-blue-600">Business</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simplify employee transportation with a business travel solution that scales with your company.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={handleGetStarted}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold rounded-xl"
                data-testid="business-get-started-btn"
              >
                Get started
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleRequestDemo}
                className="px-8 py-6 text-lg font-semibold rounded-xl"
                data-testid="business-demo-btn"
              >
                Request demo
              </Button>
              <Button
                size="lg"
                variant="ghost"
                onClick={handleLogin}
                className="px-8 py-6 text-lg font-semibold rounded-xl text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                data-testid="business-login-btn"
              >
                <LogIn className="h-5 w-5 mr-2" />
                Business Login
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything your business needs
            </h2>
            <p className="text-xl text-gray-600">
              Powerful tools to manage employee travel
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                <Building2 className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Easy setup</h3>
              <p className="text-gray-600">
                Get your team up and running in minutes with our simple onboarding.
              </p>
            </Card>

            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Employee management</h3>
              <p className="text-gray-600">
                Add or remove employees and set travel policies from one dashboard.
              </p>
            </Card>

            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                <BarChart3 className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Reporting</h3>
              <p className="text-gray-600">
                Track spending and usage with detailed analytics and reports.
              </p>
            </Card>

            <Card className="p-6 text-center space-y-4 hover:shadow-lg transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Security</h3>
              <p className="text-gray-600">
                Enterprise-grade security and compliance to keep your data safe.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Pricing tiers */}
      <div className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Flexible plans for every business
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that works best for your team
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-8 space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Starter</h3>
                <p className="text-gray-600 mt-2">For small teams</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-gray-900">K99</div>
                <div className="text-gray-600">per month</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Up to 10 employees</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Basic reporting</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Email support</span>
                </li>
              </ul>
              <Button variant="outline" onClick={handleGetStarted} className="w-full">Get started</Button>
            </Card>

            <Card className="p-8 space-y-6 border-2 border-blue-600 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Popular
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Professional</h3>
                <p className="text-gray-600 mt-2">For growing teams</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-gray-900">K299</div>
                <div className="text-gray-600">per month</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Up to 50 employees</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Advanced reporting</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Priority support</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Custom policies</span>
                </li>
              </ul>
              <Button onClick={handleGetStarted} className="w-full bg-blue-600 hover:bg-blue-700">Get started</Button>
            </Card>

            <Card className="p-8 space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Enterprise</h3>
                <p className="text-gray-600 mt-2">For large organizations</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-gray-900">Custom</div>
                <div className="text-gray-600">pricing</div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Unlimited employees</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Custom integrations</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>Dedicated support</span>
                </li>
                <li className="flex items-start gap-2 text-gray-600">
                  <span className="text-blue-600 font-bold">✓</span>
                  <span>SLA guarantees</span>
                </li>
              </ul>
              <Button variant="outline" onClick={handleContactSales} className="w-full">Contact sales</Button>
            </Card>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl lg:text-4xl font-bold text-white">
            Ready to simplify business travel?
          </h2>
          <p className="text-xl text-blue-100">
            Join thousands of companies using Tekstar for Business.
          </p>
          <Button
            size="lg"
            onClick={handleGetStarted}
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-xl"
          >
            Get started today
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Business;